import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { AlertCircle } from 'lucide-react';
import { useState } from 'react';

interface RejectionModalProps {
    isOpen: boolean;
    offerId: string;
    onConfirm: (offerId: string, reason: string) => void;
    onCancel: () => void;
}

export default function RejectionModal({ isOpen, offerId, onConfirm, onCancel }: RejectionModalProps) {
    const { t } = useTranslations();
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleConfirm = async () => {
        if (!reason.trim()) return;

        setIsSubmitting(true);
        // Simulate API call delay
        await new Promise((resolve) => setTimeout(resolve, 300));
        onConfirm(offerId, reason);
        setReason('');
        setIsSubmitting(false);
    };

    const handleCancel = () => {
        setReason('');
        onCancel();
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={handleCancel} variant="sheet">
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle className="flex items-center gap-2">
                        <AlertCircle className="text-destructive h-5 w-5 shrink-0" />
                        {t('leads.offers.reject.title', 'Reject Offer')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('leads.offers.reject.description', 'Please provide a reason for rejecting this offer.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-2">
                    <Textarea
                        placeholder={t('leads.offers.reject.placeholder', 'Explain why this offer is being rejected...')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="min-h-24 resize-none"
                    />
                    {!reason.trim() && (
                        <p className="text-destructive text-xs font-medium">{t('leads.offers.reject.required', 'Rejection reason is required')}</p>
                    )}
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button variant="outline" className="w-full md:w-auto" onClick={handleCancel}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button variant="destructive" className="w-full md:w-auto" onClick={handleConfirm} disabled={!reason.trim() || isSubmitting}>
                        {isSubmitting ? t('common.saving', 'Saving...') : t('leads.offers.reject.submit', 'Reject Offer')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
